import { useState, useEffect, useRef, useCallback } from 'react'
import { authAPI } from '@/lib/api'

interface UseVerificationPollingOptions {
  enabled: boolean
  interval?: number // Default: 5000ms (5 seconds)
  onVerified: () => void
  maxRetries?: number // Maximum consecutive failures before showing error
}

interface UseVerificationPollingReturn {
  isChecking: boolean
  lastChecked: Date | null
  error: string | null
  consecutiveFailures: number
  manualCheck: () => Promise<void>
}

/**
 * Hook to periodically check email verification status
 * Automatically calls onVerified when the user's email becomes verified
 */
export function useVerificationPolling({
  enabled,
  interval = 5000,
  onVerified,
  maxRetries = 3,
}: UseVerificationPollingOptions): UseVerificationPollingReturn {
  const [isChecking, setIsChecking] = useState(false)
  const [lastChecked, setLastChecked] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [consecutiveFailures, setConsecutiveFailures] = useState(0)
  
  // Use refs to avoid stale closures in the interval
  const onVerifiedRef = useRef(onVerified)
  const enabledRef = useRef(enabled)
  
  // Update refs when props change
  useEffect(() => {
    onVerifiedRef.current = onVerified
  }, [onVerified])
  
  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const checkVerificationStatus = useCallback(async () => {
    if (!enabledRef.current) return
    
    setIsChecking(true)
    setError(null)
    
    try {
      const response = await authAPI.getVerificationStatus()
      
      setLastChecked(new Date())
      setConsecutiveFailures(0)
      
      if (response.data?.emailConfirmed) {
        // User is now verified!
        onVerifiedRef.current()
      }
    } catch (err: any) {
      console.error('Verification status check failed:', err)
      
      setConsecutiveFailures(prev => prev + 1)
      
      // Only show error after multiple consecutive failures
      if (consecutiveFailures >= maxRetries - 1) {
        setError('Having trouble checking verification status. Please try refreshing the page.')
      }
    } finally {
      setIsChecking(false)
    }
  }, [consecutiveFailures, maxRetries])

  // Manual check function for user-triggered refresh
  const manualCheck = useCallback(async () => {
    setConsecutiveFailures(0)
    setError(null)
    await checkVerificationStatus()
  }, [checkVerificationStatus])

  // Set up polling interval
  useEffect(() => {
    if (!enabled) {
      return
    }

    // Initial check
    checkVerificationStatus()

    // Set up interval for subsequent checks
    const intervalId = setInterval(() => {
      checkVerificationStatus()
    }, interval)

    return () => {
      clearInterval(intervalId)
    }
  }, [enabled, interval, checkVerificationStatus])

  return {
    isChecking,
    lastChecked,
    error,
    consecutiveFailures,
    manualCheck,
  }
}

export default useVerificationPolling
